// REST client for the speaking coach. Everything live (audio, transcripts,
// checklist ticks) goes over the WebSocket in hooks/useVoiceLive.js instead.
//
// Same origin as the rest of the site, so there is no base URL and no CORS. It
// rides the shared axios instance from services/api.js, which attaches the admin
// bearer token to every request — the coach API is admin-only, so a bare fetch()
// would just collect 401s.

import { TOKEN_KEY } from "./api";
import axios from "axios";

const api = axios.create({ headers: { "Content-Type": "application/json" } });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A 401 here means the token expired mid-session. Clearing it and reloading
// hands the user to /signin via ProtectedRoute, rather than leaving the coach
// screen up and silently failing every poll.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.assign("/signin");
    }
    return Promise.reject(error);
  },
);

const get = async (path) => (await api.get(`/api/english${path}`)).data;
const post = async (path) => (await api.post(`/api/english${path}`)).data;
const del = async (path) => (await api.delete(`/api/english${path}`)).data;

export const fetchTopics = () => get("/topics");
export const fetchTopic = (slug) => get(`/topics/${encodeURIComponent(slug)}`);
export const resetTopic = (slug) => post(`/topics/${encodeURIComponent(slug)}/reset`);
// Omit `slug` for every topic's sessions; pass one to filter.
export const fetchSessions = (slug) =>
  get(`/sessions?limit=50${slug ? `&slug=${encodeURIComponent(slug)}` : ""}`);
export const fetchSpend = () => get("/spend");
export const fetchHealth = () => get("/health");

// --- pronunciation drill -----------------------------------------------------
// No slug anywhere: one word bank, one memory, shared by every lesson.
export const fetchVocab = (limit = 50, includeRetired = false) =>
  get(`/vocab?limit=${limit}${includeRetired ? "&include_retired=true" : ""}`);
// `verdict` is "good" or "wrong" — a query param, so the bodyless post() covers it.
export const voteWord = (word, verdict) =>
  post(`/vocab/${encodeURIComponent(word)}/vote?verdict=${verdict}`);
export const retireWord = (word) => del(`/vocab/${encodeURIComponent(word)}`);
export const restoreWord = (word) => post(`/vocab/${encodeURIComponent(word)}/restore`);
